/**
 * Contact-number field module — modular-form replacement for the legacy
 * phone field.
 *
 * Replaces:
 *  • Schema → packages/frontend/src/schema/workspaceSchema.ts:130-143
 *      phone: optional, isValidPhoneNumber test, "Contact Number is not valid"
 *  • UI → ContactDetailsForm.tsx PhoneNumberFieldV2 (label "Contact Number")
 */
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  cn,
  inputVariants,
} from '@sr/ui-library';
import { Control } from 'react-hook-form';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import * as yup from 'yup';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

interface ContactNumberFieldProps {
  control: Control<WorkspaceDetailsFormValues>;
}

/**
 * Optional phone number with country selector — same library the legacy
 * PhoneNumberFieldV2 uses.
 *
 * The container carries the ui-library Input look (inputVariants) while the
 * inner input is stripped bare (important-prefixed utilities beat the global
 * input[type='tel'] / .PhoneInput rules in index.css), so the whole control
 * reads as one input with the flag selector inside it, on the left.
 */
const ContactNumberFieldComponent = ({ control }: ContactNumberFieldProps) => (
  <FormField
    name={WORKSPACE_DETAILS_FORM_FIELDS.phone}
    control={control}
    render={({ field }) => (
      <FormItem>
        <FormLabel>Contact Number</FormLabel>
        <FormControl>
          <PhoneInput
            {...field}
            className={cn(
              inputVariants(),
              'items-center gap-2',
              'focus-within:ring-1 focus-within:ring-ring',
            )}
            defaultCountry="AU"
            numberInputProps={{
              // Important-prefixed to beat the global input[type='tel'] rules
              // in index.css (which carry their own !important).
              className:
                '!border-0 !bg-transparent !p-0 !outline-none placeholder:text-muted-foreground',
            }}
            // Overrides the spread onChange: the form types `phone` as string,
            // but PhoneInput emits undefined when cleared.
            onChange={(value) => field.onChange(value ?? '')}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

// Settings-page schema. If onboarding adopts this field with different rules,
// add a separate onboarding schema alongside (see EntityNameField pattern).
const schema = yup
  .string()
  .nullable()
  .test('phone-number-validator', 'Contact Number is not valid', (value) =>
    value == null || value === '' ? true : isValidPhoneNumber(value),
  );

type ContactNumberFieldStatics = {
  schema: yup.AnySchema;
};

type ContactNumberFieldWithStatics = typeof ContactNumberFieldComponent &
  ContactNumberFieldStatics;

export const ContactNumberField = Object.assign(ContactNumberFieldComponent, {
  schema,
}) as ContactNumberFieldWithStatics;
